import { ParsedUrlQuery } from 'querystring';

import {
  encodeQueryParams,
  decodeQueryParams,
  ObjectParam,
  StringParam,
  DelimitedArrayParam,
  QueryParamConfig,
  QueryParamConfigMap,
} from 'serialize-query-params';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import {
  hasOptionsWord,
  optionsIsNotRegularParams,
} from '@storefront/app/pdp-config/printer/state-management/pdp-config-state.utils';
type MapOption = Record<string, string>;
export type BundleOptions = {
  selectedSku?: string;
  paymentType?: string;
} & {
  [key: string]: MapOption;
};

export interface IQueryProducts {
  skus: string[];
  pdpFlow: string;
  bundleOptions: BundleOptions;
  additionalParameters?: string[];
  setBundleOption: (sku: string, product: string, value: string) => void;
  removeBundleOption: (sku: string, product: string) => void;
  getUpToDateURLQuery: () => IQueryProducts;
  toQueryParams: () => ParsedUrlQuery;
}
export const flowTypes = {
  regular: 'regular',
  hybrid: 'hybrid',
  bundleSelection: 'bundleSelection',
};

export const productTypes = {
  pc: 'pc',
  printer: 'printer',
};

type ObjectParamType = QueryParamConfig<
  { [key: string]: string },
  { [key: string]: string }
>;

const initializeQueryHandler = (query: ParsedUrlQuery) => {
  const optionsType = {} as ObjectParamType;
  const additionalParams = {} as ObjectParamType;
  const keys = Object.keys(query);
  const values = Object.values(query);
  /// initialize optionsType and additionalParams
  keys.forEach((option: string, index: number) => {
    if (hasOptionsWord(option)) (optionsType as any)[option] = ObjectParam;
    else if (optionsIsNotRegularParams(option))
      (additionalParams as any)[option] = values[index];
  });
  const params = {
    skus: DelimitedArrayParam,
    pdpFlow: StringParam,
    selectedSku: StringParam,
    ...optionsType,
  } as unknown as QueryParamConfigMap;
  const decoded = decodeQueryParams(params, query);

  const skus: string[] | undefined = decoded['skus'];
  const pdpFlow = decoded['pdpFlow'];
  const options: { [key: string]: { [key: string]: string } } = {};
  if (skus) {
    skus.forEach((sku: string) => {
      options[sku] = decoded[`options[${sku}]`] ?? {};
    });
  }
  const paramsArray = Object.keys(additionalParams);
  if (paramsArray.length) {
    paramsArray.forEach((addParam) => {
      options[addParam] = (additionalParams as any)[addParam] ?? {};
    });
  }

  return {
    skus,
    pdpFlow,
    options,
    additionalParams,
  };
};

export const getEncodedURL = (
  skus: Array<string>,
  bundleOptions: BundleOptions,
  pdpFlow: string,
  additionalParams: ObjectParamType
) => {
  const options: { [key: string]: { [key: string]: string } } = {};
  const optionstype = {};
  skus.forEach((sku: string) => {
    options[`options[${sku}]`] = bundleOptions[sku];
    (optionstype as any)[`options[${sku}]`] = ObjectParam;
  });

  const encoded = encodeQueryParams(
    {
      skus: DelimitedArrayParam,
      pdpFlow: StringParam,
      selectedSku: StringParam,
      ...optionstype,
      additionalParams: ObjectParam,
    },
    {
      skus: Array.from(skus),
      pdpFlow,
      ...options,
      ...additionalParams,
    }
  );
  return encoded;
};

export const urlQueryHandler = (query: ParsedUrlQuery) => {
  const { skus, pdpFlow, options, additionalParams } =
    initializeQueryHandler(query);
  return create(
    immer<IQueryProducts>((set, get) => ({
      skus: skus ?? [],
      pdpFlow: pdpFlow ?? flowTypes.regular,
      bundleOptions: options,
      setBundleOption: (sku: string, product: string, value: string) =>
        set((state: IQueryProducts) => {
          if (value && skus) {
            state.bundleOptions[sku][product] = value;
          }
        }),
      removeBundleOption: (sku: string, product: string) =>
        set((state: IQueryProducts) => {
          if (state.bundleOptions[sku][product]) {
            const new_state = {
              ...state.bundleOptions[sku],
            };
            delete new_state[product];
            state.bundleOptions[sku] = new_state;
          }
        }),
      toQueryParams: () => {
        const state = get();
        return getEncodedURL(
          skus,
          state.bundleOptions,
          pdpFlow,
          additionalParams
        );
      },
      getUpToDateURLQuery: () => {
        const state = get();
        return state;
      },
    }))
  );
};

export default urlQueryHandler;
